import { useQueryClient } from '@tanstack/react-query';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { DEMO_IMPORT_ISSUES, DEMO_IMPORT_SESSIONS, DEMO_IMPORT_TARGET } from '@/lib/demo-data/imports';
import {
  type ImportRowIssue,
  type ImportSession,
  type ImportTarget,
  type ImportTypeKey,
  type ImportUploadResult,
  isImportActive,
} from '@/types/import';
import { uploadImportDemoFn } from '@/services/demo/imports-demo.service';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

const IMPORTS_PATH = '/api/v1/imports';
const SESSIONS_KEY = ['import-sessions'];

/** The company group is inferred from the selected company, so the page confirms it before uploading. */
export function useImportTarget() {
  const { apiGet } = useApi();

  return useModeAwareQuery<ImportTarget, Error>(
    ['import-target'],
    async ({ signal }) => apiGet<ImportTarget>(`${IMPORTS_PATH}/target`, undefined, signal),
    DEMO_IMPORT_TARGET,
    { staleTime: 5 * 60 * 1000 },
  );
}

/**
 * Validation and applying both run on a background queue, so the list is polled while an import is
 * still working. A list of finished imports cannot change on its own, so polling stops until the
 * next upload puts something back in flight.
 */
export function useImportSessions(pollIntervalMs: number) {
  const { apiGet } = useApi();

  return useModeAwareQuery<ImportSession[], Error>(
    SESSIONS_KEY,
    async ({ signal }) => apiGet<ImportSession[]>(`${IMPORTS_PATH}/sessions`, undefined, signal),
    DEMO_IMPORT_SESSIONS,
    {
      refetchInterval: ({ state }) => (state.data?.some(isImportActive) === true ? pollIntervalMs : false),
    },
  );
}

/**
 * Only a preview is fetched: a workbook can produce thousands of issues and the results workbook is
 * what the administrator works from, so the page asks for the first {@link maxRows} of them.
 */
export function useImportSessionIssues(publicId: string | null, maxRows: number) {
  const { apiGet } = useApi();

  return useModeAwareQuery<ImportRowIssue[], Error>(
    ['import-session-issues', publicId, maxRows],
    async ({ signal }) => apiGet<ImportRowIssue[]>(`${IMPORTS_PATH}/sessions/${publicId}/issues`, { maxRows }, signal),
    DEMO_IMPORT_ISSUES,
    { enabled: publicId !== null },
  );
}

export function useUploadImport() {
  const { apiPostMultipart } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<ImportUploadResult, Error, { importType: ImportTypeKey; file: File }>(
    async ({ importType, file }) => {
      const formData = new FormData();
      formData.append('file', file);

      return apiPostMultipart<ImportUploadResult>(`${IMPORTS_PATH}/${importType}`, formData);
    },
    uploadImportDemoFn,
    { onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }) },
  );
}

export function useImportSessionAction(action: 'confirm' | 'cancel') {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (publicId) => apiPost<void>(`${IMPORTS_PATH}/sessions/${publicId}/${action}`),
    () => undefined,
    {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }),
      // A rejected action means the page was showing a state the server has already moved past.
      onError: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }),
    },
  );
}

/**
 * Templates and validation results are real workbooks generated by the API, so they are fetched as
 * a blob rather than built in the browser.
 */
export function useImportFileDownload() {
  const { apiBaseUrl, getHeaders } = useApiConfig();

  return useModeAwareMutation<void, Error, { path: string; fileName: string }>(
    async ({ path, fileName }) => {
      const headers = await getHeaders();
      const { 'Content-Type': _contentType, ...headersWithoutContentType } = headers as Record<string, string>;

      const response = await fetch(`${apiBaseUrl}${IMPORTS_PATH}${path}`, {
        method: 'GET',
        headers: headersWithoutContentType,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`The file could not be downloaded (HTTP ${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    () => undefined,
  );
}
